// om-custom-layer.ts — MapLibre CustomLayerInterface that renders a decoded
// Open-Meteo REGULAR lat/lon field on the GPU: a value (R32F) texture sampled
// over the field's geographic footprint, mapped through a 1-D colormap LUT.
//
// Ported from poc/spike/render.mjs (per-pixel headless-validated WebGL2 shader)
// and integrated with MapLibre's projection system. The spike reprojected in a
// full-screen fragment shader using a fixed Web-Mercator viewport; here we
// instead tessellate the data bbox into a grid of vertices, project each vertex
// with MapLibre's own `projectTile` (so the layer works under BOTH mercator and
// globe projection, follows the live camera, and composites correctly with
// other layers), and carry each vertex's GRID coordinate (col u, array-row
// vRow) as a varying. The fragment shader then does exactly the spike's value
// lookup + colormap, but the screen placement comes from MapLibre.
//
// REGULAR lat/lon grids only (the proven case). Bilinear value sampling +
// NEAREST colormap LUT, matching the spike.

import type { CustomLayerInterface, CustomRenderMethod, Map as MlMap } from 'maplibre-gl'
import { MercatorCoordinate } from 'maplibre-gl'
import proj4 from 'proj4'
import { buildLUT, LUT_SIZE, CLOUD_COVER, type Colormap } from './om-colormap.js'
import { makeRotatedTransform } from './om-rotated.js'

// PROJECTED-grid descriptor. When present on an OmField the renderer maps
// lon/lat -> grid via proj4 instead of the LINEAR bbox-fraction map. Built once
// in om-layer-service.updateField from the model's crs_wkt + decoded dims:
//   proj4(proj4def, [lon,lat]) -> [x,y]
//   col     = (x - x0) / dx
//   rowProj = (y - y0) / dy   (then orientation via OmField.row0South)
// where (x0,y0) is the projected SW grid corner and dx/dy the projected cell
// size (derived from the geographic-bbox corners; see om-layer-service).
export interface OmProjection {
  // proj4/PROJ.4 source-projection definition string for the model's CRS.
  // Empty for rotated-pole grids (which use `rotatedPole` instead of proj4).
  proj4def: string
  // Projected coordinates of grid column 0 / row 0 (the geographic SW corner).
  // For rotated-pole grids these are ROTATED lon/lat DEGREES (and dx/dy degrees).
  x0: number
  y0: number
  // Projected cell size (metres, or rotated-lon/lat degrees) in x (col) / y (row).
  dx: number
  dy: number
  // Rotated lat/lon (rotated-pole) descriptor. When present, per-vertex grid
  // coords map via the rotated→true sphere rotation (om-rotated) instead of proj4.
  rotatedPole?: { lonP: number; latP: number; cRotLon: number }
}

// Georeferenced field to render. REGULAR lat/lon by default; if `projection` is
// set the grid mapping is done in PROJECTED space (HRRR / MET Nordic / UKMO).
export interface OmField {
  // Row-major values, length = nLon * nLat. Index = row*nLon + col.
  values: Float32Array | Int32Array | Uint16Array | number[]
  nLon: number
  nLat: number
  // Geographic bounds [latMin, lonMin, latMax, lonMax] of the cell-CENTER
  // coordinates of the corner cells (matches geo.mjs GRID convention). For
  // projected grids this is the lat/lon footprint used to tessellate the mesh;
  // the actual grid sampling is done via `projection`.
  bbox: [number, number, number, number]
  // true if array row 0 is the SOUTH (latMin / min projected-y) edge.
  row0South: boolean
  // Optional projected-grid descriptor; when present the grid coord per vertex
  // is computed via proj4 rather than the LINEAR bbox-fraction map.
  projection?: OmProjection
  // Optional NO-DATA / fill-cell sentinel value (in the SAME units as `values`,
  // i.e. AFTER scaling). Cells equal to this (within a small epsilon) render
  // fully transparent. Regridded regional grids (e.g. ICON-D2) carry fill cells
  // in the rectangle corners where the native domain doesn't cover; their raw
  // sentinel is INT16_MAX (32767). om-layer-service converts those to NaN at
  // the raw level (handled by the shader's isnan path); `noData` is an
  // additional explicit sentinel for any model that leaves a numeric fill value.
  // Omit (undefined) for models without fill — no cell is then discarded.
  noData?: number
}

export interface OmCustomLayerOptions {
  id: string
  field: OmField
  colormap?: Colormap
  opacity?: number
  // Tessellation density of the bbox quad. Higher = closer to true mercator
  // reprojection per-pixel; the grid resolution is a good default cap.
  tessellation?: number
}

// Web Mercator is undefined at the poles: ±90° projects to infinite Y, which
// produces NaN vertices and blanks the whole mesh (e.g. global grids with a
// ±90 bbox like ICON Global). Clamp the latitude used for the MERCATOR
// coordinate to the mercator-valid range. Grid sampling (a_grid) is derived
// from the bbox fraction separately, so values stay exact — only the pole rows
// render at the mercator edge (standard for any Mercator raster).
const MERC_MAX_LAT = 85.051128779806604
// Default u_noData for fields without a declared sentinel: an extreme value no
// real reading reaches, paired with eps<0 so the shader's no-data test is dead.
const NO_MATCH_SENTINEL = 3.4e38
// Epsilon for the explicit numeric-sentinel match (scaled units). Generous
// enough to absorb R32F round-trip error on the sentinel value.
const NO_DATA_EPS = 1e-3
// MapLibre's mercator-normalized projection: lon/lat -> [x,y] in [0,1].
function lngLatToMerc(lon: number, lat: number): [number, number] {
  const clampedLat = lat > MERC_MAX_LAT ? MERC_MAX_LAT : lat < -MERC_MAX_LAT ? -MERC_MAX_LAT : lat
  const c = MercatorCoordinate.fromLngLat({ lng: lon, lat: clampedLat })
  return [c.x, c.y]
}

const VERT_BODY = `
in vec2 a_merc;     // mercator-normalized position [0,1] (-> projectTile)
in vec2 a_grid;     // grid coord: x = col u, y = array row vRow
out vec2 v_grid;
void main() {
  v_grid = a_grid;
  gl_Position = projectTile(a_merc);
}`

const FRAG = `#version 300 es
precision highp float;
in vec2 v_grid;
out vec4 outColor;

uniform sampler2D u_value;   // R32F, size nLon x nLat
uniform sampler2D u_lut;     // RGBA8, size LUT_SIZE x 1, NEAREST
uniform vec2 u_gridN;        // (nLon, nLat)
uniform float u_lutSize;
uniform float u_domainMin;
uniform float u_domainMax;
uniform float u_opacity;
uniform float u_noData;      // no-data sentinel (scaled units); never-match by default
uniform float u_noDataEps;   // tolerance around u_noData

void main() {
  float u = v_grid.x;
  float vRow = v_grid.y;

  // OUT-OF-GRID discard: a vertex's interpolated grid coord can fall outside the
  // data grid (projected/rotated footprints whose mesh slightly overhangs the
  // grid). Bounds are [-0.5, N-0.5]: each value covers a cell, so the mesh extends
  // ½ cell past the first/last cell centers (CLAMP_TO_EDGE fills that ring with the
  // edge value). Beyond ½ cell is genuine overhang — render transparent.
  if (u < -0.5 || u > u_gridN.x - 0.5 || vRow < -0.5 || vRow > u_gridN.y - 0.5) {
    outColor = vec4(0.0);
    return;
  }

  // bilinear sample R32F: texel center at (i+0.5)/N. (u,vRow) are index-space.
  vec2 texc = vec2((u + 0.5) / u_gridN.x, (vRow + 0.5) / u_gridN.y);
  float value = texture(u_value, texc).r;

  // NO-DATA / fill-cell discard: NaN (set by om-layer-service for the INT16_MAX
  // sentinel) or within eps of an explicit numeric sentinel -> transparent.
  if (isnan(value) || abs(value - u_noData) < u_noDataEps) {
    outColor = vec4(0.0);
    return;
  }

  // value -> LUT index (mirror of colormap.valueToLutIndex), NEAREST sample.
  float t = clamp((value - u_domainMin) / (u_domainMax - u_domainMin), 0.0, 1.0);
  float idx = floor(t * (u_lutSize - 1.0) + 0.5);          // round
  float lutCoord = (idx + 0.5) / u_lutSize;
  vec4 lut = texture(u_lut, vec2(lutCoord, 0.5));          // rgba (alpha ramped)

  // Honour the LUT's alpha ramp (clear sky -> a≈0 -> transparent; overcast ->
  // a≈1 -> opaque). Output PREMULTIPLIED for MapLibre's custom-layer blend
  // (gl.blendFunc = ONE, ONE_MINUS_SRC_ALPHA).
  float a = lut.a * u_opacity;
  outColor = vec4(lut.rgb * a, a);
}`

function compileShader(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type)
  if (!sh) throw new Error('om-custom-layer: createShader failed')
  gl.shaderSource(sh, src)
  gl.compileShader(sh)
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(sh)
    gl.deleteShader(sh)
    throw new Error('om-custom-layer: shader compile failed: ' + log)
  }
  return sh
}

export class OmCustomLayer implements CustomLayerInterface {
  readonly id: string
  readonly type = 'custom' as const
  readonly renderingMode = '2d' as const

  private map: MlMap | null = null
  private gl: WebGL2RenderingContext | null = null

  private field: OmField
  private colormap: Colormap
  private opacity: number
  private tessellation: number

  // GL resources (per shader variant, keyed by projection variant name).
  private programs = new Map<
    string,
    { program: WebGLProgram; uniforms: Record<string, WebGLUniformLocation | null> }
  >()
  private vao: WebGLVertexArrayObject | null = null
  private mercBuffer: WebGLBuffer | null = null
  private gridBuffer: WebGLBuffer | null = null
  private indexBuffer: WebGLBuffer | null = null
  private indexCount = 0
  private valueTex: WebGLTexture | null = null
  private lutTex: WebGLTexture | null = null
  private hasFloatLinear = false
  private geometryDirty = true
  private valueDirty = true

  constructor(opts: OmCustomLayerOptions) {
    this.id = opts.id
    this.field = opts.field
    this.colormap = opts.colormap ?? CLOUD_COVER
    this.opacity = opts.opacity ?? 1
    // 0 is the "adaptive default" sentinel (buildGeometry then uses a
    // min(grid,256) mesh). Must NOT clamp to 1 — a tessellation of 1 yields a
    // 2x2 corner-only mesh, which collapses a projected LCC fan's curved top
    // edge into a straight line between the two top corners (visible as a flat
    // cap at bbox latMax instead of the true central bulge).
    this.tessellation = Math.max(0, opts.tessellation ?? 0)
  }

  // ---- public mutators ----

  setField(field: OmField): void {
    this.field = field
    this.geometryDirty = true
    this.valueDirty = true
    this.map?.triggerRepaint()
  }

  setColormap(cmap: Colormap): void {
    this.colormap = cmap
    if (this.gl && this.lutTex) this.uploadLut(this.gl)
    this.map?.triggerRepaint()
  }

  setOpacity(opacity: number): void {
    this.opacity = Math.max(0, Math.min(1, opacity))
    this.map?.triggerRepaint()
  }

  // ---- CustomLayerInterface ----

  onAdd(map: MlMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (!(gl instanceof WebGL2RenderingContext)) {
      throw new Error('om-custom-layer requires a WebGL2 context')
    }
    this.map = map
    this.gl = gl
    // R32F linear filtering requires OES_texture_float_linear in WebGL2.
    this.hasFloatLinear = !!gl.getExtension('OES_texture_float_linear')

    this.vao = gl.createVertexArray()
    this.mercBuffer = gl.createBuffer()
    this.gridBuffer = gl.createBuffer()
    this.indexBuffer = gl.createBuffer()
    this.lutTex = gl.createTexture()
    this.valueTex = gl.createTexture()

    this.uploadLut(gl)
    this.geometryDirty = true
    this.valueDirty = true
  }

  onRemove(_map: MlMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (!(gl instanceof WebGL2RenderingContext)) return
    for (const { program } of this.programs.values()) gl.deleteProgram(program)
    this.programs.clear()
    if (this.vao) gl.deleteVertexArray(this.vao)
    if (this.mercBuffer) gl.deleteBuffer(this.mercBuffer)
    if (this.gridBuffer) gl.deleteBuffer(this.gridBuffer)
    if (this.indexBuffer) gl.deleteBuffer(this.indexBuffer)
    if (this.valueTex) gl.deleteTexture(this.valueTex)
    if (this.lutTex) gl.deleteTexture(this.lutTex)
    this.gl = null
    this.map = null
  }

  render: CustomRenderMethod = (glLike, options) => {
    const gl = this.gl
    if (!gl || glLike !== gl) return

    const variant = options.shaderData.variantName
    const prog = this.getProgram(
      gl,
      options.shaderData.vertexShaderPrelude,
      options.shaderData.define,
      variant
    )

    if (this.geometryDirty) this.buildGeometry(gl)
    if (this.valueDirty) this.uploadValues(gl)

    gl.useProgram(prog.program)
    this.bindVertexAttribs(gl, prog.program)

    // Projection uniforms expected by MapLibre's projectTile prelude.
    const pd = options.defaultProjectionData
    if (prog.uniforms.u_projection_matrix)
      gl.uniformMatrix4fv(prog.uniforms.u_projection_matrix, false, pd.mainMatrix as Float32Array)
    if (prog.uniforms.u_projection_tile_mercator_coords)
      gl.uniform4f(prog.uniforms.u_projection_tile_mercator_coords, ...pd.tileMercatorCoords)
    if (prog.uniforms.u_projection_clipping_plane)
      gl.uniform4f(prog.uniforms.u_projection_clipping_plane, ...pd.clippingPlane)
    if (prog.uniforms.u_projection_transition)
      gl.uniform1f(prog.uniforms.u_projection_transition, pd.projectionTransition)
    if (prog.uniforms.u_projection_fallback_matrix)
      gl.uniformMatrix4fv(
        prog.uniforms.u_projection_fallback_matrix,
        false,
        pd.fallbackMatrix as Float32Array
      )

    // Data / colormap uniforms.
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.valueTex)
    gl.uniform1i(prog.uniforms.u_value as WebGLUniformLocation, 0)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, this.lutTex)
    gl.uniform1i(prog.uniforms.u_lut as WebGLUniformLocation, 1)
    gl.uniform2f(prog.uniforms.u_gridN as WebGLUniformLocation, this.field.nLon, this.field.nLat)
    gl.uniform1f(prog.uniforms.u_lutSize as WebGLUniformLocation, LUT_SIZE)
    gl.uniform1f(prog.uniforms.u_domainMin as WebGLUniformLocation, this.colormap.domainMin)
    gl.uniform1f(prog.uniforms.u_domainMax as WebGLUniformLocation, this.colormap.domainMax)
    gl.uniform1f(prog.uniforms.u_opacity as WebGLUniformLocation, this.opacity)
    // No-data sentinel: when the field declares one, discard cells within eps of
    // it. Default to a value that can never match a real reading (and eps<0 so
    // the abs()<eps test is always false) -> models without fill are unaffected.
    if (this.field.noData != null && Number.isFinite(this.field.noData)) {
      gl.uniform1f(prog.uniforms.u_noData as WebGLUniformLocation, this.field.noData)
      gl.uniform1f(prog.uniforms.u_noDataEps as WebGLUniformLocation, NO_DATA_EPS)
    } else {
      gl.uniform1f(prog.uniforms.u_noData as WebGLUniformLocation, NO_MATCH_SENTINEL)
      gl.uniform1f(prog.uniforms.u_noDataEps as WebGLUniformLocation, -1.0)
    }

    gl.bindVertexArray(this.vao)
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer)
    gl.drawElements(gl.TRIANGLES, this.indexCount, gl.UNSIGNED_INT, 0)
    gl.bindVertexArray(null)
  }

  // ---- internals ----

  private getProgram(
    gl: WebGL2RenderingContext,
    prelude: string,
    define: string,
    variant: string
  ): { program: WebGLProgram; uniforms: Record<string, WebGLUniformLocation | null> } {
    const existing = this.programs.get(variant)
    if (existing) return existing

    const vertSrc = `#version 300 es\n${define}\n${prelude}\n${VERT_BODY}`
    const program = gl.createProgram()
    if (!program) throw new Error('om-custom-layer: createProgram failed')
    const vs = compileShader(gl, gl.VERTEX_SHADER, vertSrc)
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAG)
    gl.attachShader(program, vs)
    gl.attachShader(program, fs)
    gl.linkProgram(program)
    gl.deleteShader(vs)
    gl.deleteShader(fs)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      const log = gl.getProgramInfoLog(program)
      gl.deleteProgram(program)
      throw new Error('om-custom-layer: program link failed: ' + log)
    }
    const names = [
      'u_value',
      'u_lut',
      'u_gridN',
      'u_lutSize',
      'u_domainMin',
      'u_domainMax',
      'u_opacity',
      'u_noData',
      'u_noDataEps',
      'u_projection_matrix',
      'u_projection_tile_mercator_coords',
      'u_projection_clipping_plane',
      'u_projection_transition',
      'u_projection_fallback_matrix',
    ]
    const uniforms: Record<string, WebGLUniformLocation | null> = {}
    for (const n of names) uniforms[n] = gl.getUniformLocation(program, n)
    const entry = { program, uniforms }
    this.programs.set(variant, entry)
    return entry
  }

  // Tessellate the data bbox into a (cols x rows) grid of vertices. Each vertex
  // carries (a) its mercator-normalized position and (b) its grid coord (u col,
  // vRow array-row). Triangulated into two triangles per cell.
  private buildGeometry(gl: WebGL2RenderingContext): void {
    const { bbox, nLon, nLat, row0South, projection } = this.field
    const [latMin, lonMin, latMax, lonMax] = bbox

    // Vertex grid resolution: at least 2x2; default cap to the data grid size.
    const cols = this.tessellation > 0 ? this.tessellation + 1 : Math.max(2, Math.min(nLon, 256))
    const rows = this.tessellation > 0 ? this.tessellation + 1 : Math.max(2, Math.min(nLat, 256))

    const merc = new Float32Array(cols * rows * 2)
    const grid = new Float32Array(cols * rows * 2)
    let vi = 0

    if (projection) {
      // PROJECTED grid: tessellate in NATIVE GRID SPACE so the mesh exactly
      // covers the (rotated/curved-in-lat-lon) data footprint and the per-vertex
      // grid coords are exact. For each (col,row) fraction: grid index -> proj
      // (x,y) -> inverse-project to (lon,lat) -> mercator. row0South selects
      // which array row corresponds to the min-projected-y edge.
      const { proj4def, x0, y0, dx, dy, rotatedPole } = projection
      // proj -> lon/lat: proj4 for true projections, sphere rotation for rotated
      // lat/lon grids (x0/y0/dx/dy are then rotated-lon/lat DEGREES).
      let projToLngLat: (x: number, y: number) => [number, number]
      if (rotatedPole) {
        const rt = makeRotatedTransform(rotatedPole)
        projToLngLat = (x, y) => rt.toGeo(x, y)
      } else {
        const t = proj4(proj4def, 'WGS84')
        projToLngLat = (x, y) => t.forward([x, y])
      }
      for (let r = 0; r < rows; r++) {
        const fy = r / (rows - 1) // 0..1 over the data rows
        // array-row index (orientation-aware) and the matching projected y.
        // HALF-CELL EXTENSION: each value represents a cell, not a point. We span
        // grid coord [-0.5, nLat-0.5] (½ cell past the first/last cell centers) so
        // the mesh covers the full data area edge-to-edge, not center-to-center.
        // The outer ½-cell ring samples the edge cell's value (CLAMP_TO_EDGE).
        const vFromSouth = -0.5 + fy * nLat
        const vRow = row0South ? vFromSouth : nLat - 1 - vFromSouth
        // projected y for this row (south->north in proj space, ½ cell past edges).
        const yProj = y0 + vFromSouth * dy
        for (let c = 0; c < cols; c++) {
          const fx = c / (cols - 1)
          const u = -0.5 + fx * nLon // col index, ½ cell past first/last center
          const xProj = x0 + u * dx
          const [lon, lat] = projToLngLat(xProj, yProj)
          const [mx, my] = lngLatToMerc(lon, lat)
          merc[vi * 2] = mx
          merc[vi * 2 + 1] = my
          grid[vi * 2] = u
          grid[vi * 2 + 1] = vRow
          vi++
        }
      }
    } else {
      // HALF-CELL EXTENSION (see projected branch): span grid coord
      // [-0.5, N-0.5] so the mesh covers the full cell area, not center-to-center.
      const dLat = latMax - latMin
      const dLon = lonMax - lonMin
      for (let r = 0; r < rows; r++) {
        const fy = r / (rows - 1) // 0..1 from south(latMin) to north(latMax)
        // grid array-row: vFromSouth in index space, orientation-aware.
        const vFromSouth = -0.5 + fy * nLat
        const vRow = row0South ? vFromSouth : nLat - 1 - vFromSouth
        const lat = latMin + (vFromSouth / (nLat - 1)) * dLat
        for (let c = 0; c < cols; c++) {
          const fx = c / (cols - 1) // 0..1 from lonMin to lonMax
          const u = -0.5 + fx * nLon // col index, ½ cell past first/last center
          const lon = lonMin + (u / (nLon - 1)) * dLon
          const [mx, my] = lngLatToMerc(lon, lat)
          merc[vi * 2] = mx
          merc[vi * 2 + 1] = my
          grid[vi * 2] = u
          grid[vi * 2 + 1] = vRow
          vi++
        }
      }
    }

    // Index buffer (two triangles per cell), UNSIGNED_INT.
    const indices = new Uint32Array((cols - 1) * (rows - 1) * 6)
    let ii = 0
    for (let r = 0; r < rows - 1; r++) {
      for (let c = 0; c < cols - 1; c++) {
        const i0 = r * cols + c
        const i1 = i0 + 1
        const i2 = i0 + cols
        const i3 = i2 + 1
        indices[ii++] = i0
        indices[ii++] = i2
        indices[ii++] = i1
        indices[ii++] = i1
        indices[ii++] = i2
        indices[ii++] = i3
      }
    }
    this.indexCount = ii

    gl.bindBuffer(gl.ARRAY_BUFFER, this.mercBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, merc, gl.STATIC_DRAW)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.gridBuffer)
    gl.bufferData(gl.ARRAY_BUFFER, grid, gl.STATIC_DRAW)
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.indexBuffer)
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, indices, gl.STATIC_DRAW)
    this.geometryDirty = false
  }

  private bindVertexAttribs(gl: WebGL2RenderingContext, program: WebGLProgram): void {
    gl.bindVertexArray(this.vao)
    const aMerc = gl.getAttribLocation(program, 'a_merc')
    const aGrid = gl.getAttribLocation(program, 'a_grid')
    if (aMerc >= 0) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.mercBuffer)
      gl.enableVertexAttribArray(aMerc)
      gl.vertexAttribPointer(aMerc, 2, gl.FLOAT, false, 0, 0)
    }
    if (aGrid >= 0) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.gridBuffer)
      gl.enableVertexAttribArray(aGrid)
      gl.vertexAttribPointer(aGrid, 2, gl.FLOAT, false, 0, 0)
    }
    gl.bindVertexArray(null)
  }

  private uploadValues(gl: WebGL2RenderingContext): void {
    const { values, nLon, nLat } = this.field
    const fdata = values instanceof Float32Array ? values : Float32Array.from(values)
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.valueTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R32F, nLon, nLat, 0, gl.RED, gl.FLOAT, fdata)
    const filt = this.hasFloatLinear ? gl.LINEAR : gl.NEAREST
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filt)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filt)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    this.valueDirty = false
  }

  private uploadLut(gl: WebGL2RenderingContext): void {
    const lut = buildLUT(this.colormap)
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, this.lutTex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, LUT_SIZE, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, lut)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  }
}
